import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Save, Send, Eye, Languages, Sparkles } from 'lucide-react';
import { Button, addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormInput, ConfirmationModal } from '../base';
import { useNavigate } from 'react-router-dom';
import { useGuideBuilderGuide } from '../../contexts/GuideBuilderContext';
import { GuideTitleFormData, guideTitleSchema } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUpdateGuideDetails, usePublishGuide, useTranslateGuide, useGuide } from '@/hooks/useGuides';
import { GuideLanguage, GuideStepType } from '@/services/apis/guides';
import guidesApi from '@/services/apis/guides';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface GuideHeaderProps {
  onOpenChat?: () => void;
}

const GuideHeader: React.FC<GuideHeaderProps> = ({ onOpenChat }) => {
  const {
    guide,
    state: { isPreviewMode, isPublishing, isTranslating },
    actions: { onPreviewToggle, setIsPublishing, setIsTranslating, onSelectStep },
    goBackPath,
  } = useGuideBuilderGuide();
  const { title, id: guideId, steps, description } = guide;
  const isPublished = (guide as any).isPublished ?? false;
  const { currentLanguage, setLanguage } = useLanguage() as {
    currentLanguage: GuideLanguage;
    setLanguage: (lang: string) => void;
  };
  const {
    control: formControl,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm<GuideTitleFormData>({
    resolver: zodResolver(guideTitleSchema),
    defaultValues: {
      title: title || 'Guide Title',
    },
  });
  // reset form when guide changes
  useEffect(() => {
    reset({
      title: title || 'Guide Title',
    });
  }, [title, reset]);
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: updateGuideDetails } = useUpdateGuideDetails(guideId!);
  const { mutateAsync: publishGuide } = usePublishGuide(guideId!);
  const { mutateAsync: translateGuide, isPending: isTranslatingGuide } = useTranslateGuide(guideId!);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);

  // Fetch full guide data with all translations
  const { data: fullGuideResponse } = useGuide({
    id: guideId || '',
    shouldSkip: !guideId,
  });
  const fullGuide = fullGuideResponse?.data;

  // Fetch latest published version to compare with current guide
  const { data: publishedVersionResponse } = useQuery({
    queryKey: ['guide-published-version', guideId],
    queryFn: async () => {
      try {
        return await guidesApi.getLatestPublishedVersion(guideId!);
      } catch (error: any) {
        // If no published version exists, return null
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!guideId && isPublished,
    retry: false,
  });

  // Detect if there are changes from the published version
  const hasChangesFromPublished = useMemo(() => {
    if (!isPublished || !publishedVersionResponse?.data) {
      // If not published, there are no changes to detect
      return false;
    }

    const publishedSnapshot = publishedVersionResponse.data;

    // Compare translations (title, description) - use current guide title/description
    const publishedTranslation = publishedSnapshot.translations?.find((t: any) => t.language === currentLanguage);

    if (title !== publishedTranslation?.title) {
      return true;
    }
    const currentDescription = description || '';
    if (currentDescription !== (publishedTranslation?.description || '')) {
      return true;
    }

    // Compare steps count
    if (steps.length !== (publishedSnapshot.steps?.length || 0)) {
      return true;
    }

    // Compare each step
    for (let i = 0; i < steps.length; i++) {
      const currentStep = steps[i];
      const publishedStep = publishedSnapshot.steps?.[i];

      if (!publishedStep) {
        return true; // New step added
      }

      // Compare step position
      if (currentStep.position !== publishedStep.position) {
        return true;
      }

      // Compare step content
      const currentStepTranslation = currentStep.content;
      const publishedStepTranslation = publishedStep.translations?.find(
        (t: any) => t.language === currentLanguage,
      )?.content;
      if (JSON.stringify(currentStep.questionConfig) !== JSON.stringify(publishedStep.questionConfig)) {
        return true;
      }

      if (JSON.stringify(currentStepTranslation) !== JSON.stringify(publishedStepTranslation)) {
        return true;
      }

      // Compare step title
      const currentStepTitle = currentStep.title || '';
      const publishedStepTitle =
        publishedStep.translations?.find((t: any) => t.language === currentLanguage)?.title || '';
      if (currentStepTitle !== publishedStepTitle) {
        return true;
      }
    }

    return false;
  }, [isPublished, publishedVersionResponse, steps, title, description, currentLanguage]);

  const handleGoBack = () => {
    if (goBackPath) {
      navigate(goBackPath);
    } else {
      navigate(-1);
    }
  };

  const handleFormSubmit: SubmitHandler<GuideTitleFormData> = async (data) => {
    if (isPublishing) return; // Prevent saving while publishing
    try {
      await updateGuideDetails({
        lang: currentLanguage,
        data: {
          title: data.title,
        },
      });
      reset({
        title: data.title,
      });
    } catch (error: any) {
      addToast({
        title: t('guides.builder.updateError'),
        description: error.message || t('guides.builder.updateError'),
        color: 'danger',
      });
    }
  };

  const handlePublish = async () => {
    if (isPublishing) return;
    setShowPublishModal(true);
  };

  const handlePublishConfirm = async () => {
    if (isPublishing) return;
    try {
      setShowPublishModal(false);
      setIsPublishing(true);
      await publishGuide();
      // Invalidate and refetch the published version to update hasChangesFromPublished
      await queryClient.invalidateQueries({ queryKey: ['guide-published-version', guideId] });
      await queryClient.refetchQueries({ queryKey: ['guide-published-version', guideId] });
    } catch (error: any) {
      console.error('Error publishing guide:', error);
      // Error toast is handled by the mutation hook
    } finally {
      setIsPublishing(false);
    }
  };

  // Check if guide has Spanish translation
  const hasSpanishTranslation = useMemo(() => {
    return fullGuide?.translations?.some((t) => t.language === GuideLanguage.ES) ?? false;
  }, [fullGuide]);

  // Check if versions don't match (translation needed)
  const needsTranslation = useMemo(() => {
    if (currentLanguage !== GuideLanguage.EN) return false; // Only check on English version
    if (!fullGuide) return false;

    const englishTranslation = fullGuide.translations?.find((t) => t.language === GuideLanguage.EN);
    const spanishTranslation = fullGuide.translations?.find((t) => t.language === GuideLanguage.ES);
    if (!englishTranslation) return false;
    if (!spanishTranslation) return true; // Needs translation if Spanish doesn't exist

    // Compare guide translation versions
    if (englishTranslation.version !== spanishTranslation.version) {
      return true;
    }

    // Compare step translation versions
    for (const step of (fullGuide.steps || []).filter((s) =>
      [GuideStepType.RICH_TEXT, GuideStepType.QUESTION].includes(s.type),
    )) {
      const englishStepTranslation = step.translations?.find((t) => t.language === GuideLanguage.EN);
      const spanishStepTranslation = step.translations?.find((t) => t.language === GuideLanguage.ES);
      if (!englishStepTranslation) continue;
      if (!spanishStepTranslation) return true; // Needs translation if Spanish doesn't exist

      if (englishStepTranslation.version !== spanishStepTranslation.version) {
        return true;
      }
    }

    return false;
  }, [fullGuide, currentLanguage]);

  const handleTranslateToSpanish = () => {
    if (isTranslating || isTranslatingGuide) return;
    setShowTranslateModal(true);
  };

  const handleTranslateConfirm = async () => {
    if (isTranslating || isTranslatingGuide) return;
    try {
      setShowTranslateModal(false);
      setIsTranslating(true);
      await translateGuide(GuideLanguage.ES);
    } catch (error) {
      // Error toast is handled by the mutation hook
    } finally {
      setIsTranslating(false);
    }
  };

  const handleLanguageSwitch = () => {
    const newLanguage = currentLanguage === GuideLanguage.EN ? GuideLanguage.ES : GuideLanguage.EN;
    setLanguage(newLanguage);
    onSelectStep(undefined);
    addToast({
      title: t('guides.builder.header.languageSwitched'),
      description: t('guides.builder.header.viewingLanguage', {
        language: newLanguage === GuideLanguage.EN ? t('common.english') : t('common.spanish'),
      }),
      color: 'default',
      icon: <Languages className="w-5 h-5" />,
    });
  };

  // Determine publish button state and text
  const publishButtonText = useMemo(() => {
    if (isPublishing) {
      return t('guides.builder.header.publishing');
    }
    if (!isPublished) {
      return t('guides.builder.header.publish');
    }
    if (hasChangesFromPublished) {
      return t('guides.builder.header.publishChanges') || 'Publish Changes';
    }
    return t('guides.builder.header.published') || 'Published';
  }, [isPublishing, isPublished, hasChangesFromPublished, t]);

  const isPublishButtonDisabled = isPublishing || isTranslating || (isPublished && !hasChangesFromPublished);
  const isTranslateButtonDisabled =
    isTranslating || isTranslatingGuide || !needsTranslation || currentLanguage !== GuideLanguage.EN;
  // Publish confirmation message
  const publishMessage = useMemo(() => {
    const baseMessage =
      hasSpanishTranslation && currentLanguage === GuideLanguage.EN
        ? t('guides.builder.header.publishConfirmWithTranslation', {
            default:
              'Are you sure you want to publish this guide? Publishing means learners will see these changes. You have a Spanish translation available. Would you like to review it before publishing?',
          })
        : t('guides.builder.header.publishConfirm', {
            default: 'Are you sure you want to publish this guide? Publishing means learners will see these changes.',
          });

    if (hasSpanishTranslation && currentLanguage === GuideLanguage.EN) {
      return (
        <>
          <p>{baseMessage}</p>
          <div className="mt-4">
            <Button
              variant="bordered"
              onPress={() => {
                setShowPublishModal(false);
                handleLanguageSwitch();
              }}
              startContent={<Languages className="w-4 h-4" />}
            >
              {t('guides.builder.header.reviewSpanishVersion') || 'Review Spanish Version'}
            </Button>
          </div>
        </>
      );
    }
    return baseMessage;
  }, [hasSpanishTranslation, currentLanguage, t]);

  return (
    <>
      <header className="bg-background text-foreground border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center space-x-4">
            <Button
              isIconOnly
              variant="light"
              onPress={handleGoBack}
              className="text-gray-600"
              title={t('common.back')}
              aria-label={t('common.back')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <form onSubmit={handleSubmit(handleFormSubmit)} className="w-[45vw] space-x-2">
              <FormInput
                name="title"
                control={formControl}
                placeholder={t('guides.builder.header.editTitlePlaceholder')}
                variant="flat"
                classNames={{
                  input: 'text-xl font-semibold',
                  inputWrapper: 'bg-transparent shadow-none border-none',
                }}
                disabled={isPublishing || isTranslating}
              />
              {isDirty && !isPublishing && !isTranslating && (
                <Button
                  disabled={!isValid}
                  type="submit"
                  isLoading={isSubmitting}
                  color="primary"
                  size="md"
                  startContent={<Save className="w-12 h-12" />}
                >
                  Save
                </Button>
              )}
            </form>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              onPress={onPreviewToggle}
              variant={isPreviewMode ? 'solid' : 'bordered'}
              color={isPreviewMode ? 'primary' : 'default'}
              startContent={<Eye className="w-4 h-4" />}
              isDisabled={isPublishing || isTranslating}
            >
              {t('guides.builder.header.preview')}
            </Button>
            {hasSpanishTranslation && (
              <div className="flex items-center gap-1 bg-content2 rounded-lg p-1">
                <Button
                  isIconOnly
                  size="sm"
                  variant={currentLanguage === GuideLanguage.EN ? 'solid' : 'light'}
                  color={currentLanguage === GuideLanguage.EN ? 'primary' : 'default'}
                  onPress={() => {
                    if (currentLanguage !== GuideLanguage.EN) {
                      handleLanguageSwitch();
                    }
                  }}
                  isDisabled={isPublishing || isTranslating || isTranslatingGuide}
                  aria-label="English"
                >
                  <span className="text-xs">EN</span>
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  variant={currentLanguage === GuideLanguage.ES ? 'solid' : 'light'}
                  color={currentLanguage === GuideLanguage.ES ? 'primary' : 'default'}
                  onPress={() => {
                    if (currentLanguage !== GuideLanguage.ES) {
                      handleLanguageSwitch();
                    }
                  }}
                  isDisabled={isPublishing || isTranslating || isTranslatingGuide}
                  aria-label="Spanish"
                >
                  <span className="text-xs">ES</span>
                </Button>
              </div>
            )}

            <Button
              onPress={onOpenChat}
              variant="bordered"
              startContent={<Sparkles className="w-4 h-4" />}
              aria-label="Open AI Chat"
            >
              AI Assistant
            </Button>
            {currentLanguage === GuideLanguage.EN && (
              <>
                <Button
                  onPress={handleTranslateToSpanish}
                  variant="bordered"
                  isLoading={isTranslating || isTranslatingGuide}
                  startContent={!isTranslating && !isTranslatingGuide && <Languages className="w-4 h-4" />}
                  disabled={isTranslateButtonDisabled}
                  isDisabled={isTranslateButtonDisabled}
                >
                  {t('guides.builder.header.translateToSpanish') || 'Translate to Spanish'}
                </Button>
              </>
            )}
            <Button
              onPress={handlePublish}
              isLoading={isPublishing}
              color="primary"
              disabled={isPublishButtonDisabled}
              isDisabled={isPublishButtonDisabled}
              startContent={!isPublishing && <Send className="w-4 h-4" />}
            >
              {publishButtonText}
            </Button>
          </div>
        </div>
      </header>

      <ConfirmationModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublishConfirm}
        title={t('guides.builder.header.publishConfirmTitle') || 'Publish Guide'}
        message={publishMessage}
        confirmLabel={t('guides.builder.header.publishConfirmButton') || 'Publish'}
        cancelLabel={t('common.cancel')}
        confirmColor="primary"
        isLoading={isPublishing}
      />

      <ConfirmationModal
        isOpen={showTranslateModal}
        onClose={() => setShowTranslateModal(false)}
        onConfirm={handleTranslateConfirm}
        title={t('guides.builder.header.translateConfirmTitle') || 'Translate to Spanish'}
        message={
          t('guides.builder.header.translateConfirmMessage') ||
          'This will generate a Spanish translation of your guide using AI. Any existing Spanish translations will be overwritten with the new translation. Are you sure you want to continue?'
        }
        confirmLabel={t('guides.builder.header.translateConfirmButton') || 'Translate'}
        cancelLabel={t('common.cancel')}
        confirmColor="primary"
        isLoading={isTranslating || isTranslatingGuide}
      />
    </>
  );
};

export default GuideHeader;
